import type { ArticleBody } from './types';

export const GUIDES: Record<string, ArticleBody> = {
  'preparing-for-an-assessment': {
    intro: [
      'Developmental appointments are often short and hard to get. A little preparation means the professional sees your child as they really are, and you leave with answers and a plan.',
    ],
    extra: [
      {
        heading: 'Before the visit',
        blocks: [
          [
            'Write down your three biggest worries, in order. Start the appointment with them.',
            'Note examples with dates: “Doesn’t answer to his name, even when we stand behind him and call loudly.”',
            'Take short videos of the things that worry you; children often behave differently in a clinic.',
            'Collect earlier reports, hearing and vision tests, school notes and your Nurture report.',
            'Ask relatives or the teacher what they notice. It helps to know whether things happen in more than one place.',
          ],
        ],
      },
      {
        heading: 'During the visit',
        blocks: [
          [
            'Bring snacks, a favourite toy and a spare set of clothes.',
            'It is fine to say “That’s not what he’s usually like.”',
            'Ask the professional to write down any terms they use, so you can read about them later.',
          ],
        ],
      },
      {
        heading: 'Questions worth asking',
        blocks: [
          [
            'What do you think is going on, and how sure are you?',
            'What should we do at home, starting this week?',
            'Which therapies or support would help, how often, and where can we get them?',
            'What should we watch for, and when should we come back?',
            'Is there anything the school should know or do?',
          ],
        ],
      },
    ],
    nurture: 'Your Nurture report has a page written for professionals: what you told us, area by area, in their terms. Print it or share it on WhatsApp before the visit.',
    sources: [{ label: 'Rashtriya Bal Swasthya Karyakram (Government of India)', href: 'https://rbsk.mohfw.gov.in/' }],
  },

  'who-can-help': {
    intro: ['Finding the right professional can feel like a maze. Here is who does what, so you know where to start. Many children see more than one.'],
    extra: [
      {
        heading: 'Doctors',
        blocks: [
          [
            '**Paediatrician**: your first stop for any worry about growth, health or development. Can refer you onward.',
            '**Developmental paediatrician**: a paediatrician with extra training in development; assesses and diagnoses conditions such as autism, ADHD and developmental delay, and coordinates the plan.',
            '**Child psychiatrist**: a doctor who treats emotional, behavioural and mental-health conditions, and can prescribe medicine when needed.',
            '**Paediatric neurologist**: for seizures, loss of skills, or concerns about the brain and nerves.',
          ],
        ],
      },
      {
        heading: 'Therapists and educators',
        blocks: [
          [
            '**Child or clinical psychologist**: assesses learning, thinking and behaviour; offers therapy for anxiety, behaviour and emotions; parent training.',
            '**Speech and language therapist**: understanding, talking, social communication, and feeding and swallowing.',
            '**Occupational therapist (OT)**: everyday skills: handwriting, dressing, eating, play, and sensory needs.',
            '**Physiotherapist**: movement, balance, strength and posture.',
            '**Special educator**: teaching approaches for children who learn differently; support at school.',
            '**Audiologist**: hearing tests, for any child with speech delay.',
          ],
        ],
      },
      {
        heading: 'Checking qualifications in India',
        blocks: [
          'Psychologists, special educators and several therapy professions in India are registered with the [Rehabilitation Council of India](https://rehabcouncil.nic.in/). It is reasonable to ask a professional about their qualifications and registration.',
          'Government District Early Intervention Centres (DEICs) under the [Rashtriya Bal Swasthya Karyakram](https://rbsk.mohfw.gov.in/) offer free screening and early intervention for young children.',
        ],
      },
    ],
    nurture: 'Your report suggests which kind of professional fits what stood out, and you can keep your child’s care team in the app.',
    sources: [
      { label: 'Rehabilitation Council of India', href: 'https://rehabcouncil.nic.in/' },
      { label: 'Rashtriya Bal Swasthya Karyakram (Government of India)', href: 'https://rbsk.mohfw.gov.in/' },
    ],
  },

  'udid-and-school-rights': {
    intro: [
      'India’s **Rights of Persons with Disabilities Act, 2016** recognises 21 disabilities, including autism spectrum disorder, specific learning disabilities, intellectual disability, speech and language disability, and cerebral palsy. It gives children rights to inclusive education and reasonable support.',
      'This page is general information, not legal advice. Rules and procedures can differ by state; check with your state’s disability department or your child’s school.',
    ],
    extra: [
      {
        heading: 'The UDID card',
        blocks: [
          'The Unique Disability ID (UDID) card is a single national disability certificate and ID. It can help with school support, exam accommodations and government schemes.',
          [
            'Apply online at [swavlambancard.gov.in](https://www.swavlambancard.gov.in/).',
            'You will need a photo, proof of identity and address, and any medical reports you have.',
            'You will be asked to visit a government hospital or medical board for assessment; the certificate states the type and percentage of disability.',
            'Keep copies of every report and the application number.',
          ],
        ],
      },
      {
        heading: 'At school',
        blocks: [
          [
            'Schools funded or recognised by the government must admit children with disabilities without discrimination and provide reasonable accommodation.',
            'Ask the school about reasonable accommodations, for example extra time, a reader or scribe, or seating near the teacher, and what they need from you to arrange them.',
            'Exam boards have their own rules for accommodations; ask the school early, well before board exams.',
            'Put requests in writing and keep copies of replies.',
          ],
        ],
      },
    ],
    nurture: 'A Nurture report is not a disability certificate, but its care-team page is a useful summary to bring when you ask for an assessment.',
    sources: [
      { label: 'Department of Empowerment of Persons with Disabilities; Acts', href: 'https://depwd.gov.in/acts/' },
      { label: 'UDID portal (Swavlamban Card)', href: 'https://www.swavlambancard.gov.in/' },
    ],
  },

  'caring-for-yourself': {
    intro: [
      'Raising a child with extra needs can be joyful and exhausting at the same time. Appointments, therapies, advice from everyone, worry about the future. It adds up. Looking after yourself isn’t selfish; it is part of looking after your child.',
    ],
    extra: [
      {
        heading: 'Small things that help',
        blocks: [
          [
            'Share the load: give a partner, grandparent or friend one regular job (a therapy run, bath time).',
            'Keep ten minutes a day that are just yours; tea on the balcony counts.',
            'Find other parents who understand. Therapy waiting rooms and parent groups can become lifelines.',
            'Say no to advice that makes you feel judged. You know your child.',
            'Sleep and eat as regularly as you can; they affect your patience more than anything.',
          ],
        ],
      },
      {
        heading: 'When it’s more than tired',
        blocks: [
          'If you feel low most days, can’t enjoy anything, feel hopeless, or have thoughts of harming yourself, please reach out now. **Tele-MANAS 14416** is free, confidential and available 24×7 in many Indian languages.',
        ],
      },
    ],
    nurture: 'ZenQuip, the company behind Nurture, is a mental-health service. You are not alone in this.',
    sources: [{ label: 'Tele-MANAS (Government of India)', href: 'https://telemanas.mohfw.gov.in/' }],
  },

  'milestones-what-to-expect': {
    intro: [
      'Children grow at their own pace, and a wide range is normal. Milestones are a guide, not a test. What matters most is whether your child keeps gaining new skills, and whether something seems different from before or from other children the same age.',
    ],
    extra: [
      {
        heading: 'Around 18 months',
        blocks: [
          [
            'Points to show you things, and looks at you to share what they see.',
            'Says several single words and understands simple requests like “give me the ball”.',
            'Walks steadily and starts to run. Tries to feed themselves.',
            'Copies what you do, such as sweeping or talking on the phone.',
          ],
        ],
      },
      {
        heading: 'Around 2 years',
        blocks: [
          [
            'Puts two words together, like “more milk”.',
            'Points to body parts and pictures when you name them.',
            'Runs, kicks a ball and climbs onto low furniture.',
            'Starts pretend play, like feeding a doll, and shows interest in other children.',
          ],
        ],
      },
      {
        heading: 'Around 3 years',
        blocks: [
          [
            'Speaks in short sentences, and people outside the family understand much of it.',
            'Plays make-believe, and takes turns with a little help.',
            'Climbs stairs, jumps with both feet and may pedal a tricycle.',
            'Shows a range of feelings and can be comforted.',
          ],
        ],
      },
      {
        heading: 'Around 4 to 5 years',
        blocks: [
          [
            'Tells a simple story and answers “why” questions.',
            'Draws a person with a head, body and arms or legs.',
            'Dresses with little help and uses the toilet.',
            'Plays with other children and follows simple rules in games.',
          ],
        ],
      },
      {
        heading: 'Talk to a professional sooner if',
        blocks: [
          [
            'Your child loses a skill they had, such as words, eye contact or a favourite way of playing. Do not wait with this one, at any age.',
            'They do not turn to their name or to everyday sounds.',
            'There is no pointing or showing by 18 months, no single words by 16 months or no two-word phrases by 2 years.',
            'They are not walking by 18 months.',
            'They show very little interest in people, or ordinary sounds, touch or changes upset them a great deal.',
            'You simply feel that something is different. Trust that feeling and ask.',
          ],
        ],
      },
    ],
    nurture: 'The Nurture assessment asks about the milestones for your child’s age and shows, area by area, what is on track and what is worth watching or discussing with a professional.',
    sources: [
      { label: 'CDC: Learn the Signs. Act Early.', href: 'https://www.cdc.gov/act-early/' },
      { label: 'Rashtriya Bal Swasthya Karyakram (Government of India)', href: 'https://rbsk.mohfw.gov.in/' },
    ],
  },

  'why-early-support-matters': {
    intro: [
      'A young child’s brain learns faster than at any later age. Support that starts early has more time to help, and it costs families less worry than months of wondering. That is the reason to ask sooner rather than later.',
    ],
    extra: [
      {
        heading: 'What early support really is',
        blocks: [
          [
            'Talking, singing, reading and playing together every day. This is the core of it.',
            'Small changes to routines that make the day easier for your child.',
            'When needed, therapy such as speech, occupational or behavioural therapy, with practice at home between sessions.',
            'Support does not depend on a label. You can begin helping before any diagnosis.',
          ],
        ],
      },
      {
        heading: 'Why “wait and see” can cost time',
        blocks: [
          [
            'Appointments and therapy places often have waiting lists. Starting the process early means the wait passes while you are already helping at home.',
            'An assessment gives information, not a verdict. It tells you where your child needs support and where they are strong.',
            'Skills build on each other. Help with early communication or play makes later learning easier.',
          ],
        ],
      },
      {
        heading: 'It is never too late',
        blocks: [
          'Children make progress at every age. If your child is older, begin now. Earlier simply means more time, not that later is too late.',
        ],
      },
      {
        heading: 'Where to begin',
        blocks: [
          [
            'Start with your paediatrician or family doctor and ask for a hearing and vision check, since both affect speech and learning.',
            'Ask at your district hospital whether a District Early Intervention Centre (DEIC) is available. These centres under the Rashtriya Bal Swasthya Karyakram screen children and support early intervention.',
            'Bring your Nurture report to any appointment. It gives the professional a clear summary.',
          ],
        ],
      },
    ],
    nurture: 'A Nurture report is a starting point for that conversation. It shows which areas are on track and which are worth a closer look, with ideas to try at home while you arrange an appointment.',
    sources: [{ label: 'Rashtriya Bal Swasthya Karyakram (Government of India)', href: 'https://rbsk.mohfw.gov.in/' }],
  },

  'myths-and-facts': {
    intro: [
      'Families hear a lot of advice about children who develop differently, and some of it is wrong. Some myths cause guilt, and others cause delay. Here are the ones we hear most, with what is actually known.',
    ],
    myths: [
      { myth: 'Autism is caused by bad parenting or a cold mother.', fact: 'No. Autism comes from differences in how the brain develops, and it often runs in families. Parents do not cause it.' },
      { myth: 'Vaccines cause autism.', fact: 'Large studies in many countries have found no link between vaccines and autism. Please keep your child’s vaccinations up to date.' },
      { myth: 'He will talk when he is ready. Boys always talk late.', fact: 'Some children do catch up, but nobody can tell in advance which ones. A hearing check and a developmental check are quick, and they show whether help is needed.' },
      { myth: 'A child who makes eye contact or has friends cannot be autistic.', fact: 'Children differ widely. Signs can be quieter, especially in girls, and many are missed for years.' },
      { myth: 'Children with ADHD are just naughty or lazy.', fact: 'Attention and impulse control develop differently in ADHD. These children need understanding and support, not more punishment.' },
      { myth: 'A label will harm my child, so it is better not to ask.', fact: 'Not knowing harms more than knowing. A clear picture is what opens the door to support, school help and understanding at home.' },
      { myth: 'A special diet or supplement can cure autism or ADHD.', fact: 'No diet or supplement has been shown to cure either. Please talk to a doctor before cutting foods out, because children still need a balanced diet to grow.' },
      { myth: 'Screens cause autism.', fact: 'There is no evidence that screens cause autism. Long hours on a screen can still crowd out talking and play, which children need.' },
    ],
    nurture: 'The Nurture report looks at what you tell us about your child and explains it in plain words. It is not a diagnosis, and it does not label your child.',
    sources: [{ label: 'World Health Organization: autism spectrum disorders', href: 'https://www.who.int/news-room/fact-sheets/detail/autism-spectrum-disorders' }],
  },

  'talking-to-family-and-friends': {
    intro: [
      'Grandparents, aunts, neighbours and other parents often mean well, but comments like “he is just spoilt” or “you are worrying too much” can hurt. A few simple words, said calmly, can change how family understands your child.',
    ],
    extra: [
      {
        heading: 'Simple things you can say',
        blocks: [
          [
            '“We are working with a doctor to understand how he learns best.”',
            '“She needs a bit more time and a different kind of help. That is what we are giving her.”',
            '“What helps most is patience and a calm voice. Thank you for trying.”',
            '“We would like to handle this our own way. We will tell you if we need advice.”',
          ],
        ],
      },
      {
        heading: 'Bringing grandparents along',
        blocks: [
          [
            'Invite them to watch a therapy session or a home activity, so they see what works.',
            'Share one or two simple tips, such as giving a warning before a change, rather than a long list.',
            'Let them share the good moments too. Point out what your child has learned lately.',
            'You can share a page of your Nurture report on WhatsApp if it helps them see the same picture.',
          ],
        ],
      },
      {
        heading: 'When your child struggles in public',
        blocks: [
          [
            'You do not have to explain to strangers. A short line is enough: “He is having a hard moment. He will be fine.”',
            'Focus on your child first. Move to a quieter spot if you can.',
            'Ask the people who are close to you to step in and help, not to comment.',
          ],
        ],
      },
      {
        heading: 'You decide what to share',
        blocks: [
          'Your child’s health details are private. Share only what you want to, and only with people who will treat it kindly. Use words your child would be comfortable hearing.',
        ],
      },
    ],
    nurture: 'The Nurture family feature lets you give another adult read-only access to your child’s reports, so they see the same picture you do.',
    sources: [{ label: 'Tele-MANAS (Government of India)', href: 'https://telemanas.mohfw.gov.in/' }],
  },

  'supporting-brothers-and-sisters': {
    intro: [
      'When one child needs extra time and attention, brothers and sisters notice. They may feel proud, confused, jealous or left out, sometimes all in one day. A little attention aimed at them makes a big difference.',
    ],
    extra: [
      {
        heading: 'Explain in words they understand',
        blocks: [
          [
            'Tell them simply how their brother or sister is different: “Her brain finds loud noises hard, so she covers her ears.”',
            'Say clearly that it is nobody’s fault, and that it is not catching.',
            'Answer questions honestly, and let them ask again later.',
          ],
        ],
      },
      {
        heading: 'Protect one-to-one time',
        blocks: [
          [
            'Ten minutes a day that is only theirs, with no phone, is worth more than a big outing once a month.',
            'Let them choose the activity.',
            'Celebrate their own achievements as loudly as you would celebrate anyone’s.',
          ],
        ],
      },
      {
        heading: 'Give them a role, but not a burden',
        blocks: [
          [
            'Brothers and sisters can be wonderful play partners. Invite them to join in activities.',
            'Do not make them the carer. Childhood should stay childhood.',
            'Fair does not always mean equal. Explain that each child gets what they need.',
          ],
        ],
      },
      {
        heading: 'Watch for signs they are struggling',
        blocks: [
          'A sudden change in mood, school marks, sleep or eating can mean they are carrying worry. Talk to them, and ask their school counsellor or your doctor if it lasts.',
        ],
      },
    ],
    nurture: 'The Nurture home programme suggests short activities that a brother or sister can join, so play time becomes practice time for everyone.',
    sources: [{ label: 'Tele-MANAS (Government of India)', href: 'https://telemanas.mohfw.gov.in/' }],
  },

  'routines-that-help-at-home': {
    intro: [
      'Many children feel calmer and learn faster when the day is predictable. A routine does not need to be strict. It needs to be clear, and the same most days.',
    ],
    extra: [
      {
        heading: 'Make the day visible',
        blocks: [
          [
            'Use a simple picture schedule on the wall: wake up, brush, breakfast, play, bath, story, sleep. Photos or drawings both work.',
            'Take a picture off or turn it over when each step is done. Your child can see what is finished and what comes next.',
            'Keep it to four or five steps at first.',
          ],
        ],
      },
      {
        heading: 'Make changes easier',
        blocks: [
          [
            'Give a warning before a change: “Five more minutes, then bath.” A timer helps.',
            'Use “first, then”: “First shoes, then park.”',
            'Tell your child about a change in advance, such as a visitor or a different school bus.',
          ],
        ],
      },
      {
        heading: 'Start small and keep going',
        blocks: [
          [
            'Choose the one time of day that is hardest, often mornings or bedtime, and work on that first.',
            'Do the same steps in the same order for two weeks before you judge whether it helps.',
            'Praise the effort, not just the result. “You put your shoes on by yourself!”',
            'Leave room for rest and free play. A routine is a frame, not a timetable.',
          ],
        ],
      },
    ],
    nurture: 'The Nurture home programme places each week’s activities into times of the day you already have, and includes a simple routine and a fridge sheet for the hard moments.',
    sources: [{ label: 'CDC: Learn the Signs. Act Early.', href: 'https://www.cdc.gov/act-early/' }],
  },

  'more-than-one-language': {
    intro: [
      'Many Indian children grow up hearing two, three or more languages. Families sometimes worry that this slows talking, and are told to speak just one. The evidence says otherwise.',
    ],
    myths: [
      { myth: 'Speaking more than one language at home causes speech delay.', fact: 'It does not. Children can learn several languages at once. A real delay would show in all of their languages, and it needs a check-up whatever language is spoken.' },
      { myth: 'We should stop using our home language and use only English.', fact: 'Keep using the language you are most fluent in. Rich, natural talk in that language helps your child more than stumbling English.' },
    ],
    extra: [
      {
        heading: 'What helps',
        blocks: [
          [
            'Talk, sing and read in the language you speak best. Warm, natural conversation is what builds language.',
            'Each person can stick to their own language, or families can mix. Either works.',
            'Count all of your child’s languages together when you think about how many words they know.',
            'Use stories, songs and rhymes from your own tradition. They are full of language.',
          ],
        ],
      },
      {
        heading: 'When to ask a professional',
        blocks: [
          [
            'Your child is not using single words by about 16 months, or two-word phrases by about 2 years, in any language.',
            'They seem not to understand simple requests in any language.',
            'They lose words they used to say.',
            'Ask for a speech and language therapist who understands your home languages, or who can involve you in therapy in the language you speak.',
          ],
        ],
      },
    ],
    nurture: 'The Nurture assessment asks about communication for your child’s age. Answer about how they communicate overall, in whichever languages they use.',
    sources: [{ label: 'American Speech-Language-Hearing Association', href: 'https://www.asha.org/' }],
  },

  'keeping-your-child-safe': {
    intro: [
      'Some children, especially those who are very active, do not sense danger, or love to run off, need extra safety planning. A few simple steps give you peace of mind without turning home into a fortress.',
    ],
    extra: [
      {
        heading: 'At home',
        blocks: [
          [
            'Fit window grills and balcony guards, and keep the main door latched high up.',
            'Keep medicines, cleaning products and sharp things out of reach or locked away.',
            'A door alarm or chime tells you when someone goes out.',
            'Keep a recent photo of your child on your phone.',
          ],
        ],
      },
      {
        heading: 'Outside and in crowds',
        blocks: [
          [
            'Hold hands near roads. Practise “stop” and “wait” as a game.',
            'Put an ID band or card with your phone number on your child, or sewn into their clothes.',
            'Tell people you trust, such as neighbours, the school bus staff and shopkeepers, that your child may wander, and ask them to call you.',
          ],
        ],
      },
      {
        heading: 'Water',
        blocks: [
          [
            'Water is the biggest risk for children who wander. Keep buckets, tubs and tanks covered or empty.',
            'Always stay within arm’s reach at pools, ponds and beaches, even with a lifejacket.',
            'A swimming lesson with an instructor who understands your child is a lifelong safety skill.',
          ],
        ],
      },
      {
        heading: 'If your child goes missing',
        blocks: [
          [
            'Call **112** at once. Do not wait to see if they come back.',
            'Check water first, even if your child has never gone near it before.',
            'Tell the police your child’s name, age, clothes, and what may help, such as a love of certain places or difficulty speaking.',
          ],
        ],
      },
      {
        heading: 'Other numbers to keep',
        blocks: [
          [
            '**Childline 1098** is for any child in need of care and protection.',
            '**Tele-MANAS 14416** is free, confidential and open 24×7 if you or a family member needs to talk to someone.',
          ],
        ],
      },
    ],
    nurture: 'The Nurture care-team page and report tell you what you might mention to your doctor. Add anything safety-related that worries you, such as running off or not sensing danger.',
    sources: [{ label: 'Tele-MANAS (Government of India)', href: 'https://telemanas.mohfw.gov.in/' }],
  },
};
